import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth-lib';
import { Card, StatusPill } from '../../design-system';
import { canAccessNavKey } from './navItems';
import { AUTOMATIC_NOTIFICATIONS } from '../../lib/notificationSettings';
import './AutomaticNotifications.css';

// The other half of "notifications": the ones that go out without anybody
// writing them. See lib/notificationSettings.js for why this sits on the
// broadcast composer rather than on a settings page of its own.
//
// ⚠️ A REAL <Link>, NOT A BUTTON WITH navigate(). These are destinations —
// middle-click, ⌘-click and "copy link address" are how somebody hands the DSC
// schedule to the person who actually owns it, and an onClick handler on a
// <button> answers none of them.
//
// ⚠️ THE STATE LINE IS A COURTESY; THE LINK IS THE POINT. Each entry fetches
// its own settings and a failure is swallowed — a toast reading "could not load
// the DSC schedule" on the composer is noise about a screen the reader has not
// opened, and the row still links where it said it would.

function AutomaticNotifications() {
  const { user } = useAuth();
  const items = useMemo(
    () => AUTOMATIC_NOTIFICATIONS.filter((item) => canAccessNavKey(user, item.navKey)),
    [user]
  );
  const [state, setState] = useState({});

  useEffect(() => {
    let alive = true;
    items.forEach((item) => {
      item.load()
        .then((s) => { if (alive) setState((prev) => ({ ...prev, [item.key]: s })); })
        .catch(() => {});
    });
    return () => { alive = false; };
  }, [items]);

  // Nothing this reader may open. An empty card headed "Sent automatically" is
  // worse than no card: it says the firm has none, which is untrue.
  if (!items.length) return null;

  return (
    <Card>
      <h3 className="an-h">Sent automatically</h3>
      <p className="an-sub">
        These go out on a schedule, with nobody writing them. If you came here to change when a
        reminder lands, it is set on its own screen.
      </p>

      <ul className="an-list">
        {items.map((item) => {
          const s = state[item.key];
          return (
            <li key={item.key}>
              <Link to={item.to} className="an-row">
                <span className="an-row-body">
                  <span className="an-row-head">
                    <span className="an-name">{item.name}</span>
                    {s && <StatusPill variant={s.variant} label={s.label} />}
                  </span>
                  <span className="an-what">{item.what}</span>
                  {s && <span className={`an-detail an-detail-${s.variant}`}>{s.detail}</span>}
                </span>
                <span className="an-go" aria-hidden="true">→</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export default AutomaticNotifications;
